import { z } from "zod";
import { dynamicChoiceCatalogSchema } from "./dynamic-choices";
import {
  dataKeySchema,
  integrationRecordIdSchema,
  integrationDataSourceSchema,
  integrationDataPointerSchema,
  type IntegrationDataSource,
} from "./integration-data-policy";
export * from "./integration-data-policy";
export const integrationDataValueSchema = z.union([
  z.string().max(32768),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(z.string().max(32768)).max(1000),
  dynamicChoiceCatalogSchema,
]);
export const integrationDataValuesSchema = z.record(
  dataKeySchema,
  integrationDataValueSchema,
);
export const integrationHostDataSchema = z.record(
  dataKeySchema,
  z.record(integrationRecordIdSchema, integrationDataValuesSchema),
);
export const integrationDataQuerySchema = z
  .object({
    sourceId: dataKeySchema,
    recordId: integrationRecordIdSchema,
    fields: z.array(dataKeySchema).min(1).max(100),
    parameters: z
      .record(
        dataKeySchema,
        z.union([
          z.string().max(1000),
          z.number().finite(),
          z.boolean(),
          z.null(),
        ]),
      )
      .default({}),
  })
  .strict();
export type IntegrationDataQuery = z.output<typeof integrationDataQuerySchema>;
export type IntegrationDataValues = z.output<
  typeof integrationDataValuesSchema
>;

/** Bounded, own-property JSON Pointer selection; no expressions or executable paths. */
export function integrationDataPointer(data: unknown, path: string): unknown {
  integrationDataPointerSchema.parse(path);
  let value = data;
  for (const key of path === ""
    ? []
    : path
        .slice(1)
        .split("/")
        .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))) {
    if (!value || typeof value !== "object" || !Object.hasOwn(value, key))
      return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}
export function mapIntegrationData(
  source: IntegrationDataSource,
  raw: unknown,
  fields: string[],
): IntegrationDataValues {
  const result: Record<string, unknown> = Object.create(null);
  for (const key of fields) {
    const field = source.fields.find((field) => field.key === key);
    if (!field) throw Error("INTEGRATION_DATA_FIELD_DENIED");
    const value = integrationDataPointer(raw, field.path);
    if (value === undefined) throw Error("INTEGRATION_DATA_FIELD_MISSING");
    if (field.mode === "value") {
      if (value && typeof value === "object" && !Array.isArray(value))
        throw Error("INTEGRATION_DATA_VALUE_INVALID");
      result[key] = value;
    } else {
      if (!Array.isArray(value) || value.length > 1000)
        throw Error("INTEGRATION_DATA_CHOICES_INVALID");
      const selected =
        field.selectedPath !== undefined
          ? integrationDataPointer(raw, field.selectedPath)
          : undefined;
      if (field.selectedPath !== undefined && selected === undefined)
        throw Error("INTEGRATION_DATA_FIELD_MISSING");
      result[key] = dynamicChoiceCatalogSchema.parse({
        kind: "dynamic-choices",
        version: 1,
        ...(field.selectedPath !== undefined ? { value: selected } : {}),
        choices: value.map((row) => ({
          value: integrationDataPointer(row, field.valuePath),
          label: integrationDataPointer(row, field.labelPath),
        })),
      });
    }
  }
  return integrationDataValuesSchema.parse(result);
}
